/**
 * Marine domain pure helpers — catalog-row predicates, line formatting, and
 * boat-attribute mapping.
 *
 * These live together because they share one property: no dependencies, so the
 * Node ESM harness can import and test them directly. The HTTP layer that
 * fetches boat reference data lives in `src/services/marineReferenceService.js`,
 * which imports HTTPService and therefore cannot be harness-tested.
 *
 * A marine service is an ORDINARY installer_services price row whose
 * `vehicle_size` is the literal 'SF' and whose `service_cost` is a
 * PER-SQUARE-FOOT RATE rather than a job price. The payload is otherwise
 * shape-identical to a car service, which makes the distinction easy to lose —
 * so it lives here and nowhere else.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/marineService.test.mjs`. Do not add imports.
 */

/** The literal `vehicle_size` value the server uses for area-priced rows. */
export const MARINE_VEHICLE_SIZE = 'SF';

/**
 * The catalog payload carries `asset_type: "marine"` as of server commit
 * 137fd36. `asset_type` WINS when present, so the older `vehicle_size === 'SF'`
 * sentinel can be retired later without touching a single call site.
 *
 * A stored/refetched LINE row (`GET /requestService/:requestId`,
 * `GET /requestService/technician/:id`) is not guaranteed to carry the catalog
 * fields above, but it does carry the persisted `request_services.pricing_kind`
 * ENUM('unit','area') column — `'area'` is used ONLY for area-priced marine
 * lines. Accept both `pricing_kind` and camelCase `pricingKind`, since the
 * codebase sends either spelling depending on the call site.
 */
export function isMarineService(svc) {
  if (!svc) return false;
  if (svc.asset_type) return String(svc.asset_type).toLowerCase() === 'marine';
  if (String(svc.vehicle_size ?? '').trim().toUpperCase() === MARINE_VEHICLE_SIZE) return true;
  const pricingKind = svc.pricing_kind ?? svc.pricingKind;
  return String(pricingKind ?? '').trim().toLowerCase() === 'area';
}

/**
 * Is this row PRICED BY AREA? Deliberately NOT keyed on `asset_type`.
 *
 * `isMarineService` answers "is this a boat service" and short-circuits on
 * `asset_type`, which makes it wrong for an area-priced AUTOMOTIVE service - a
 * box truck wrap is `asset_type: 'auto'` and priced by the square foot, and
 * would fall through to flat pricing with an $8.50 rate rendered as a job price.
 *
 * The server reached the same conclusion from the other side: every downstream
 * consumer (duration, scheduling, invoices, quantity labels) keys off
 * `pricing_kind` and none of them mentions `asset_type`.
 *
 * `pricing_kind` now rides the CATALOG payload too (`master_services.pricing_kind`),
 * alongside `unit_of_measure`, so both a catalog row and the line it becomes
 * carry it. `vehicle_size === 'SF'` is the pre-migration fallback and applies ONLY when no
 * `pricing_kind` is present. When one is present it wins outright: a Flat marine
 * service (spec 2026-09-11 D7) is an SF row with `pricing_kind='unit'`.
 */
export function isAreaPriced(svc) {
  if (!svc) return false;
  const pricingKind = svc.pricing_kind ?? svc.pricingKind;
  const kind = String(pricingKind ?? '').trim().toLowerCase();
  // An explicit kind is authoritative. A Flat marine service is an SF row with
  // pricing_kind 'unit' — the sentinel alone would misread it as area and send
  // it to the calculator with no formula.
  if (kind) return kind === 'area';
  // Pre-migration fallback: rows that carry no pricing_kind at all.
  return String(svc.vehicle_size ?? '').trim().toUpperCase() === MARINE_VEHICLE_SIZE;
}

/**
 * The per-sq-ft rate for a marine row; null for anything else.
 *
 * `service_cost` CANNOT be trusted here. It carries the per-sq-ft rate on a
 * CATALOG row but the LINE TOTAL on a confirmed estimate line — the calculator
 * overwrites it on purpose (`newServiceForm.jsx:766`), because
 * calculateServiceTotal, discounts and the final amount all read it. The rate
 * survives on `unit_price`.
 *
 * So read `unit_price` first, exactly as every other line-price read in the
 * codebase does (`newServiceForm.jsx:1159`, `displayJob.js:212`), and fall back
 * to `service_cost` for catalog rows, which have no `unit_price`. Getting this
 * backwards printed "220 sq ft @ $1870.00/sq ft" — the total, labelled a rate.
 *
 * Zero/blank `unit_price` falls through rather than reporting $0.00/sq ft.
 */
export function getMarineRate(svc) {
  if (!isMarineService(svc)) return null;
  const unit = Number(svc.unit_price);
  if (Number.isFinite(unit) && unit > 0) return unit;
  const rate = Number(svc.service_cost ?? svc.base_price);
  return Number.isFinite(rate) ? rate : null;
}

/**
 * What a marine line's rate is priced PER — `'ft'` (linear, qty = CEIL(LOA)),
 * `'sq_ft'` (area), or `'each'` (flat / component). Read from the server's
 * `unit_of_measure` column, which now rides both the catalog and the line reads
 * (snake or camel).
 *
 * `pricing_kind='area'` covers BOTH `ft` and `sq_ft`, so it cannot label the
 * unit — only this can. Absent/null falls back to `'sq_ft'`: the server sends
 * NULL pre-migration, where every marine line was square-foot priced, so the
 * historical assumption stays correct. The only caller is marine-gated.
 */
export function marineUnit(svc) {
  const raw = svc && (svc.unit_of_measure ?? svc.unitOfMeasure);
  const u = String(raw ?? '').trim().toLowerCase();
  return (u === 'ft' || u === 'sq_ft' || u === 'each') ? u : 'sq_ft';
}

/** The human unit label for a `unit_of_measure`. `each` has none — it is a count. */
function unitLabel(unit) {
  return unit === 'ft' ? 'ft' : unit === 'each' ? '' : 'sq ft';
}

/** Display label for a service's unit: `'ft'` | `'sq ft'` | `''` (each). */
export function marineUnitLabel(svc) {
  return unitLabel(marineUnit(svc));
}

/**
 * "540 sq ft" / "65 ft" — the quantity with its unit. `unit` defaults to
 * `'sq_ft'` so existing single-arg callers keep today's behaviour.
 */
export function formatArea(quantity, unit = 'sq_ft') {
  const n = Number(quantity);
  if (!Number.isFinite(n)) return '';
  const label = unitLabel(unit);
  const qty = n.toLocaleString('en-US');
  return label ? `${qty} ${label}` : qty;
}

/**
 * The breakdown shown beside a marine line: "540 sq ft @ $5.00/sq ft" or
 * "65 ft @ $285.00/ft". A flat/component (`each`) line has no area breakdown —
 * return '' so the caller renders it as an ordinary unit line. A line with an
 * explicit `pricing_kind` other than `'area'` returns '' too.
 */
export function formatMarineLine(line) {
  if (!isMarineService(line)) return '';
  // An explicit non-area kind is a plain unit line — Flat or By size (spec
  // 2026-09-11 D8) — so no area breakdown, whatever its vehicle_size says.
  // Lines with no kind keep the historical behaviour.
  const kind = String(line.pricing_kind ?? line.pricingKind ?? '').trim().toLowerCase();
  if (kind && kind !== 'area') return '';
  const unit = marineUnit(line);
  if (unit === 'each') return '';
  const rate = getMarineRate(line);
  const qty = formatArea(line?.quantity, unit);
  if (rate == null) return qty;
  return `${qty} @ $${rate.toFixed(2)}/${unitLabel(unit)}`;
}

// ---------------------------------------------------------------------------
// Boat attributes — mapping a /api/boats/years row onto the vehicle payload
// ---------------------------------------------------------------------------

/**
 * Build the `marine_attributes` satellite payload from a chosen /years row.
 *
 * `hull_height_in` is null for EVERY feed-supplied row — no feed carries
 * freeboard. Carry the null through faithfully rather than substituting a
 * default: the area calculator prompts for it, and what the estimator enters is
 * learned back onto the row server-side.
 *
 * Emits `boat_model_uuid` + `boat_model_source` — the VENDOR identifiers, never
 * the server's internal row id, so a reference-table reload cannot silently
 * re-point a saved boat.
 */
export function buildMarineAttributes(yearRow, options) {
  const row = yearRow || {};
  const hin = options && options.hin;
  return {
    boat_class: row.boat_class ?? null,
    loa_ft: row.loa_ft ?? null,
    beam_ft: row.beam_ft ?? null,
    hull_height_in: row.hull_height_in ?? null,
    hull_material: row.hull_code ?? null,
    boat_model_uuid: row.boat_model_uuid ?? null,
    boat_model_source: row.boat_model_source ?? 'vehdb',
    hin: hin ? String(hin).trim().toUpperCase() : null,
  };
}

/**
 * The marine fields the server reads. `Vehicle.extractMarineAttributes` takes
 * these off the TOP LEVEL of the request body and never descends into a nested
 * object, so a `marine_attributes: {...}` payload yields null for every one of
 * them and the boat is written as having supplied no marine data at all.
 */
export const MARINE_WIRE_FIELDS = [
  'boat_class', 'loa_ft', 'beam_ft', 'hull_height_in', 'hull_material',
  'engine_hours', 'category', 'hin', 'hin_verified', 'boat_model_source', 'boat_model_uuid',
];

/**
 * Read a vehicle's marine attributes from BOTH shapes — nested under
 * `marine_attributes` (what the wizard builds) and flat on the vehicle itself
 * (what the server stores, and what a re-fetched row looks like).
 *
 * Every consumer goes through this rather than reaching for `.marine_attributes`
 * directly, so a saved boat loaded back from the API behaves like a fresh one.
 *
 * BOTH, not either. An earlier version returned the nested object whole the
 * moment one was present, which made a PRESENT-BUT-EMPTY `marine_attributes`
 * mask populated flat columns — and that is exactly the shape a re-fetched row
 * can have, because `toVehiclePayload` flattens the fields for the write
 * (`Vehicle.extractMarineAttributes` reads the top level and never descends),
 * so the nested column is written by nobody while the columns beside it are
 * correct. The calculator then opened on a saved boat with every dimension
 * blank. A null inside the nested object must not erase a real value either:
 * `buildMarineAttributes` emits an explicit null for every field the reference
 * row lacks, so nulls there mean "unknown", never "empty".
 *
 * Flat first, nested overlaid — a wizard object carries only the nested shape,
 * so it is unaffected, and where both are populated the wizard's is the fresher.
 */
export function marineAttrsOf(vehicle) {
  const v = vehicle || {};
  const out = {};
  MARINE_WIRE_FIELDS.forEach((key) => {
    if (v[key] !== undefined && v[key] !== null) out[key] = v[key];
  });
  const nested = v.marine_attributes;
  if (nested && typeof nested === 'object') {
    Object.keys(nested).forEach((key) => {
      if (nested[key] !== undefined && nested[key] !== null) out[key] = nested[key];
    });
  }
  return out;
}

/**
 * The freeboard an estimator entered into the area calculator, read back off the
 * confirmed marine lines.
 *
 * No feed carries hull height, so `boat_model.hull_height_in` starts NULL and
 * the calculator is the only place the number is ever captured. The server
 * learns it back onto the reference row from `POST /vehicles` — but only when
 * `hull_height_in` and `boat_model_uuid` both arrive — so this is what closes
 * the loop. Without it every estimator re-measures the same boat forever.
 *
 * First confirmed line wins. Two marine lines on one estimate describe the same
 * hull, so they cannot legitimately disagree; if they do, the first is as good
 * a choice as any and picking deterministically beats picking the larger.
 */
export function learnedHullHeight(services) {
  if (!Array.isArray(services)) return null;
  for (const svc of services) {
    if (!svc || !isMarineService(svc)) continue;
    const vars = svc.marine_variables || svc.marineVariables;
    if (!vars || typeof vars !== 'object') continue;
    // NEVER learn a value the calculator derived rather than a human measuring.
    // The calculator seeds an empty freeboard from length overall, and learning
    // that back would write a guess onto the reference row as though it were
    // measured — after which it is indistinguishable from data, for every future
    // boat of that model. A blank row is a known gap; a learned estimate is a gap
    // that reports itself as filled, which is strictly worse.
    if (vars.hull_height_estimated === true) continue;
    const h = Number(vars.hull_height_in);
    if (Number.isFinite(h) && h > 0) return h;
  }
  return null;
}

/**
 * Shape a vehicle for `POST /vehicles`: marine attributes FLATTENED onto the top
 * level, nested key removed.
 *
 * Pass the estimate's `services` to backfill a freeboard the estimator entered
 * in the calculator. Only fills a GAP — a hull height already on the reference
 * row always wins, so this can never overwrite known data with a typed guess.
 * And only a MEASURED one: `learnedHullHeight` skips any value the calculator
 * derived from length, so an estimate never reaches the reference data.
 *
 * The client keeps them grouped under `marine_attributes` because that is a
 * useful unit to pass around (the calculator reads it as one object). The server
 * wants them flat. This is the one place those two shapes meet — flatten at the
 * boundary rather than in the wizard, so there is a single tested chokepoint
 * instead of a convention every call site has to remember.
 *
 * Non-marine vehicles pass through untouched.
 */
export function toVehiclePayload(vehicle, services) {
  const v = vehicle || {};
  const attrs = v.marine_attributes;
  if (!attrs || typeof attrs !== 'object') return { ...v };
  const { marine_attributes, ...rest } = v;
  const flat = { ...rest };
  MARINE_WIRE_FIELDS.forEach((key) => {
    if (attrs[key] !== undefined) flat[key] = attrs[key];
  });
  if (flat.hull_height_in == null) {
    const learned = learnedHullHeight(services);
    if (learned != null) flat.hull_height_in = learned;
  }
  return flat;
}

/**
 * The modern USCG hull identification number format, in full:
 *
 *   BUJ   31234   A   323
 *   MIC   serial  mo  year
 *
 *   MIC     3 letters, manufacturer identifier. No I or O.
 *   serial  5 alphanumerics, manufacturer's own. No I, O or Q.
 *   month   A-L, the month of certification (A = January).
 *   year    3 digits: certification year digit + 2-digit model year.
 *
 * THE MIC AND SERIAL CLASSES ARE DELIBERATELY ASYMMETRIC. This is not a typo —
 * do not "fix" it back into symmetry. Per 33 CFR § 181.25, the serial block
 * (characters 4-8) excludes I, O AND Q, because all three are easily misread
 * as 1 or 0. The MIC (characters 1-3) is a Coast Guard-assigned manufacturer
 * code and is not governed by that clause — it excludes only I and O, and Q is
 * legal there. Ruled on explicitly by the product owner; see
 * `isValidHin rejects I and O anywhere, and Q in the serial block` in
 * `scripts/test/marineService.test.mjs` for the boundary case that pins this.
 *
 * SYNTAX ONLY. A HIN carries no check digit, so there is nothing to compute and
 * nothing to decode — unlike a VIN, this can never be verified against a
 * reference database. Deliberately NOT routed through
 * `src/components/common/validateVIN.jsx`, which applies a 17-character VIN
 * check digit a HIN does not have.
 *
 * This pattern REJECTS real boats: pre-1984 hulls predate the format entirely,
 * and many imports do not follow it. That is why check-in offers an explicit
 * override rather than treating a rejection as final — see `HinStep.jsx`.
 *
 * Replaced the advisory `hinWarning()`, which passed anything 12 chars long.
 * Two HIN validators that disagree in one module is a trap; there is one.
 */
export const HIN_PATTERN = /^[A-HJ-NP-Z]{3}[A-HJ-NPR-Z0-9]{5}[A-L][0-9]{3}$/;

/** Trim + uppercase, then test. Never throws; anything non-string is invalid. */
export function isValidHin(hin) {
  return HIN_PATTERN.test(String(hin ?? '').trim().toUpperCase());
}

/** The sizes a By-size marine service is priced at, and the only `sizeTier` values the catalog read accepts. */
export const BOAT_SIZE_TIERS = ['S', 'M', 'L', 'XL'];

/**
 * A boat size tier, normalised — or '' when the value is not one. The marine
 * catalog read 400s (`INVALID_SIZE_TIER`) on anything outside BOAT_SIZE_TIERS,
 * so every value bound for that read goes through here first.
 */
export function boatSizeTier(value) {
  const tier = String(value ?? '').trim().toUpperCase();
  return BOAT_SIZE_TIERS.includes(tier) ? tier : '';
}

// ---------------------------------------------------------------------------
// Catalog fetch params
// ---------------------------------------------------------------------------

/**
 * The size/asset filter block shared by the three Step-3 catalog URL builders.
 *
 * A boat has no body style, so the automotive bodyStyle-or-vehicleSize pair
 * does not apply. The marine read takes `sizeTier` (S/M/L/XL): without one it
 * returns the SF rows — Flat and Measured services — and with one it adds that
 * size's By-size rows (server answer 2026-09-11,
 * `tasks/client-prompt-marine-pricing-types.md`). Decide area-vs-unit from
 * `pricing_kind` on the rows, never from `vehicle_size`.
 *
 * Sending vehicleSize anyway for auto produced the literal string
 * `vehicleSize=undefined`, because URLSearchParams stringifies `undefined`
 * rather than omitting it — which is why this is tested rather than inlined.
 */
export function catalogFilterParams(params) {
  const p = params || {};
  const assetType = p.assetType || 'auto';
  if (assetType === 'marine') {
    // vehicleSize=SF is ignored by the 2026-09-11 read and kept so an older
    // server still returns the SF rows (Flat + Measured). sizeTier adds the
    // By-size rows at that size; it is omitted unless it is a real tier,
    // because the read 400s (INVALID_SIZE_TIER) on anything else.
    const tier = boatSizeTier(p.sizeTier);
    return tier
      ? { assetType, vehicleSize: MARINE_VEHICLE_SIZE, sizeTier: tier }
      : { assetType, vehicleSize: MARINE_VEHICLE_SIZE };
  }
  return {
    ...(p.bodyStyle
      ? { bodyStyle: p.bodyStyle }
      : { vehicleSize: p.vehicleSize }),
    assetType,
  };
}

/**
 * Is a boat complete enough to move past the vehicle step?
 *
 * The automotive gate requires trim, body and id_car_trim, because the car
 * service catalog is keyed on body style. A boat has none of those — the marine
 * catalog is keyed on assetType + vehicle_size 'SF' — so requiring them blocks
 * the Next button forever.
 *
 * IDENTITY ONLY. Deliberately NOT `loa_ft`, `beam_ft` or `boat_class`.
 *
 * Dimensions are not a property of the vehicle; they are inputs that PARTICULAR
 * area formulas consume, and the calculator already asks for exactly the ones
 * the chosen coverage needs (`formulaDimensions`) and refuses to price without
 * them. A MEASURED_AREA service never reads LOA or beam at all. Requiring them
 * here demands data at the wrong moment on behalf of a service that may never
 * be selected.
 *
 * It is also unworkable against real reference data: of 628 models, 237 carry a
 * beam and 382 a length, so the stricter gate left roughly half the catalog
 * with a permanently dead Next button and no explanation.
 *
 * An earlier version required `beam_ft` because POST /vehicles was recorded as
 * 400ing without it. If that is still true the save fails instead — which is
 * why `tasks/server-prompt-boat-optional-dimensions.md` asks for the write side
 * to accept nulls. Watch the save, not this predicate, if boats stop persisting.
 */
export function isMarineVehicleComplete(vehicle) {
  const v = vehicle || {};
  return Boolean(v.year && v.make && v.model);
}
